'use client'

import { useState } from 'react'
import { buildCoordVersion, buildEventMapImageUrl } from '@/lib/api/event-maps'

interface EventLocationMapProps {
  eventPublicId: string
  latitude: number | null | undefined
  longitude: number | null | undefined
  locationText: string | null
  locationDisplayName: string | null | undefined
}

/** Google Maps search URL for a free-text query — mirrors the admin Athletics schedule directions link (`buildMapsUrl`). */
function buildDirectionsUrl(query: string): string {
  return `https://maps.google.com/?q=${encodeURIComponent(query)}`
}

/**
 * Static-map picture rendered below the public page's location-text row
 * (plan 2026-07-20-002, U6). A thin client island — the page stays a Server
 * Component — so the U4 proxy image can carry an `onError` fallback.
 *
 * No coordinates → renders nothing; the existing text-only row above is
 * unchanged (R3). With coordinates, the whole picture is a directions link
 * (R6); on a load failure (proxy 5xx, a stale 204 slipping through, or a
 * network blip) it reverts to a plain text-plus-directions-link presentation
 * rather than a broken-image icon next to a real address.
 */
export function EventLocationMap({
  eventPublicId,
  latitude,
  longitude,
  locationText,
  locationDisplayName
}: EventLocationMapProps) {
  const [failed, setFailed] = useState(false)
  if (latitude == null || longitude == null) return null

  const label = locationDisplayName ?? locationText ?? 'Event location'
  const directionsUrl = buildDirectionsUrl(label)

  if (failed) {
    return (
      <a
        href={directionsUrl}
        target="_blank"
        rel="noreferrer"
        className="text-primary mb-3 inline-block text-sm font-semibold hover:underline"
      >
        {label}
      </a>
    )
  }

  return (
    <a
      href={directionsUrl}
      target="_blank"
      rel="noreferrer"
      aria-label={`Get directions to ${label}`}
      className="border-border mb-3 block overflow-hidden rounded-[10px] border"
    >
      <img
        src={buildEventMapImageUrl(eventPublicId, 'web_card', buildCoordVersion(latitude, longitude))}
        alt={label}
        className="h-[180px] w-full object-cover"
        onError={() => setFailed(true)}
      />
    </a>
  )
}
